// ============================================
// FILE: /frontend/src/components/admin/IncomePieChart.jsx
// PURPOSE: Income breakdown table with month selector
// ============================================

import React, { useState, useMemo } from 'react';
import { TrendingUp, X, ChevronDown } from 'lucide-react';

// Color palette for each income stream
const COLORS = {
  BRONZE: '#CD7F32',
  SILVER: '#C0C0C0',
  GOLD: '#FFD700',
  PLATINUM: '#E5E4E2',
  FEATURED_7D: '#10B981',
  FEATURED_14D: '#3B82F6',
  FEATURED_21D: '#8B5CF6',
};

const PLAN_LABELS = {
  BRONZE: 'Bronze Subscription',
  SILVER: 'Silver Subscription',
  GOLD: 'Gold Subscription',
  PLATINUM: 'Platinum Subscription',
  FEATURED_7D: 'Featured 7 Days',
  FEATURED_14D: 'Featured 14 Days',
  FEATURED_21D: 'Featured 21 Days',
};

export default function IncomePieChart({ payments }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');

  // Get unique months from payment data
  const availableMonths = useMemo(() => {
    const months = new Set();
    payments.forEach(payment => {
      if (payment.status === 'COMPLETED') {
        const date = new Date(payment.createdAt);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        months.add(monthKey);
      }
    });
    return Array.from(months).sort().reverse();
  }, [payments]);

  // Filter payments by selected month
  const filteredPayments = useMemo(() => {
    if (selectedMonth === 'all') {
      return payments.filter(p => p.status === 'COMPLETED');
    }
    return payments.filter(p => {
      if (p.status !== 'COMPLETED') return false;
      const date = new Date(p.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return monthKey === selectedMonth;
    });
  }, [payments, selectedMonth]);

  // Calculate income breakdown
  const incomeData = useMemo(() => {
    const breakdown = {};
    let total = 0;

    filteredPayments.forEach(payment => {
      const plan = payment.plan;
      if (!breakdown[plan]) {
        breakdown[plan] = 0;
      }
      breakdown[plan] += Number(payment.amount);
      total += Number(payment.amount);
    });

    // Convert to array format for recharts
    return Object.entries(breakdown)
      .map(([plan, amount]) => ({
        name: PLAN_LABELS[plan] || plan,
        value: amount,
        percentage: total > 0 ? ((amount / total) * 100).toFixed(1) : 0,
        color: COLORS[plan] || '#94A3B8'
      }))
      .sort((a, b) => b.value - a.value); // Sort by highest grossing
  }, [filteredPayments]);

  const totalIncome = incomeData.reduce((sum, item) => sum + item.value, 0);

  // Format month label
  const getMonthLabel = (monthKey) => {
    if (monthKey === 'all') return 'All Time';
    const [year, month] = monthKey.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md"
      >
        <TrendingUp className="w-5 h-5" />
        <span className="font-semibold">View Income Breakdown</span>
      </button>
    );
  }

  return (
    <div className="border rounded-lg p-4 sm:p-6 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg sm:text-xl font-bold flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Income Breakdown
        </h3>
        <button
          onClick={() => setIsOpen(false)}
          className="p-2 hover:bg-gray-100 rounded-md transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Month Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Select Period</label>
        <div className="relative">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full px-4 py-2 pr-10 border rounded-lg appearance-none bg-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
          >
            <option value="all">All Time</option>
            {availableMonths.map(month => (
              <option key={month} value={month}>
                {getMonthLabel(month)}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Total for Selected Period */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
        <p className="text-sm text-muted-foreground mb-1">Total Income ({getMonthLabel(selectedMonth)})</p>
        <p className="text-2xl sm:text-3xl font-bold text-primary">KES {totalIncome.toLocaleString()}</p>
        <p className="text-xs text-muted-foreground mt-1">{filteredPayments.length} completed transactions</p>
      </div>

      {incomeData.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">No income data for selected period</p>
      ) : (
        <>
          {/* Compact Breakdown Table */}
          <div>
            <h4 className="font-semibold text-sm mb-2">Income by Source (Highest to Lowest)</h4>
            <div className="space-y-1">
              {incomeData.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 hover:bg-gray-50 rounded border"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="font-medium text-xs truncate">{item.name}</span>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="font-bold text-xs">KES {item.value.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{item.percentage}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}